import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Divider,
} from '@mui/material';
import { ArrowBack, SmartToy, Computer, Code, Person, Schedule, Language, Storage, MoreVert, Label } from '@mui/icons-material';
import { projectsAPI } from '../services/api';
import { Project } from '../types';

interface ServerIpAddress {
  _id: string;
  ipAddress: string;
  projectCount: number;
  lastActivity: string;
  projects: string[];
  customName?: string;
}

interface ProjectListProps {
  category: 'robots' | 'servers';
  onProjectSelect: (project: Project) => void;
  onBack: () => void;
}

const ProjectList: React.FC<ProjectListProps> = React.memo(({ category, onProjectSelect, onBack }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [serverIpAddresses, setServerIpAddresses] = useState<ServerIpAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<{ ipAddress: string; type: 'robot' | 'server' } | null>(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (category === 'servers') {
        // Fetch IP addresses for servers
        const response = await projectsAPI.getServerIpAddresses();
        const serversWithCustomNames = response.data.map((server: any) => {
          // Load custom name from localStorage if available
          const storageKey = `customName_server_${server.ipAddress}`;
          const storedName = localStorage.getItem(storageKey);
          return {
            ...server,
            customName: storedName || server.customName
          };
        });
        setServerIpAddresses(serversWithCustomNames);
      } else {
        // Fetch projects for robots
        const response = await projectsAPI.getProjects(category === 'robots');
        const projectsWithCustomNames = response.data.map((project: any) => {
          // Load custom name from localStorage if available
          const storageKey = `customName_robot_${project.ipAddress}`;
          const storedName = localStorage.getItem(storageKey);
          return {
            ...project,
            customName: storedName || project.customName
          };
        });
        setProjects(projectsWithCustomNames);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to fetch ${category === 'servers' ? 'server IP addresses' : 'projects'}`);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group robots by IP address
  const groupRobotsByIpAddress = (robots: Project[]) => {
    const grouped = robots.reduce((acc, robot) => {
      const ipAddress = robot.ipAddress || 'Unknown IP';
      if (!acc[ipAddress]) {
        acc[ipAddress] = {
          ipAddress,
          robots: [],
          totalCommits: 0,
          lastActivity: '',
          latestCommit: {
            commitHash: '',
            commitMessage: '',
            authorName: '',
            commitTimestamp: '',
            branchName: ''
          },
          customName: robot.customName
        };
      }
      
      acc[ipAddress].robots.push(robot);
      acc[ipAddress].totalCommits += robot.totalCommits;
      
      // Keep the most recent activity
      if (!acc[ipAddress].lastActivity || new Date(robot.lastActivity) > new Date(acc[ipAddress].lastActivity)) {
        acc[ipAddress].lastActivity = robot.lastActivity;
      }
      
      // Keep the most recent commit
      if (!acc[ipAddress].latestCommit.commitTimestamp || 
          new Date(robot.latestCommit.commitTimestamp) > new Date(acc[ipAddress].latestCommit.commitTimestamp)) {
        acc[ipAddress].latestCommit = robot.latestCommit;
      }
      
      return acc;
    }, {} as Record<string, {
      ipAddress: string;
      robots: Project[];
      totalCommits: number;
      lastActivity: string;
      latestCommit: any;
      customName?: string;
    }>);
    
    return Object.values(grouped);
  };

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, ipAddress: string, type: 'robot' | 'server') => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedItem({ ipAddress, type });
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedItem here - let it persist until dialog is closed
  };

  // Handle add/update server name click
  const handleAddServerName = () => {
    console.log('🔵 Update Server Name clicked, selectedItem:', selectedItem);
    const item = selectedItem;
    
    // Close menu first
    setAnchorEl(null);
    
    if (item) {
      // Get existing custom name if available
      let existingName = '';
      if (item.type === 'server') {
        const server = serverIpAddresses.find(s => s.ipAddress === item.ipAddress);
        existingName = server?.customName || '';
      } else {
        const groupedRobots = groupRobotsByIpAddress(projects);
        const robot = groupedRobots.find(r => r.ipAddress === item.ipAddress);
        existingName = robot?.customName || '';
      }
      
      // Also check localStorage as fallback
      if (!existingName) {
        const storageKey = `customName_${item.type}_${item.ipAddress}`;
        existingName = localStorage.getItem(storageKey) || '';
      }
      
      console.log('🔵 Setting custom name:', existingName);
      setCustomName(existingName);
      console.log('🔵 Opening dialog...');
      setDialogOpen(true);
    } else {
      console.error('❌ No item selected when Update Server Name clicked!');
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setCustomName('');
    // Don't clear selectedItem here - it will be cleared after save completes
  };

  // Handle save custom name
  const handleSaveCustomName = async () => {
    console.log('💾 Save button clicked!', { selectedItem, customName });
    
    if (!selectedItem) {
      console.error('❌ No selected item!');
      return;
    }

    try {
      setSaving(true);
      console.log('⏳ Saving custom name...');
      
      // Try to call API to save custom name
      try {
        console.log('📡 Attempting API call...');
        await projectsAPI.saveCustomName(selectedItem.ipAddress, customName, selectedItem.type);
        console.log('✅ API call successful');
      } catch (apiError) {
        console.warn('⚠️ API call failed, using localStorage fallback:', apiError);
        // Fallback to localStorage if API is not available
        const storageKey = `customName_${selectedItem.type}_${selectedItem.ipAddress}`;
        localStorage.setItem(storageKey, customName || '');
        console.log('💾 Saved to localStorage:', storageKey, customName);
      }
      
      // Update local state
      if (selectedItem.type === 'server') {
        console.log('🔄 Updating server state...');
        setServerIpAddresses(prev => 
          prev.map(server => 
            server.ipAddress === selectedItem.ipAddress 
              ? { ...server, customName: customName || undefined } 
              : server
          )
        );
      } else {
        console.log('🔄 Updating robot state...');
        // For robots, we need to update the projects array
        setProjects(prev => 
          prev.map(project => 
            project.ipAddress === selectedItem.ipAddress 
              ? { ...project, customName: customName || undefined } 
              : project
          )
        );
      }
      
      console.log('✅ Custom name saved successfully!');
      setSnackbar({ 
        open: true, 
        message: 'Custom name saved successfully!', 
        severity: 'success' 
      });
      
      // Close dialog and clear state after successful save
      setDialogOpen(false);
      setCustomName('');
      setSelectedItem(null);
    } catch (error: any) {
      console.error('❌ Error saving custom name:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to save custom name. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Don't show full screen loading - let the page structure render
  // if (loading) {
  //   return (
  //     <Box
  //       display="flex"
  //       justifyContent="center"
  //       alignItems="center"
  //       minHeight="100vh"
  //       sx={{ background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)',
        p: 4,
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            mb: 2,
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          Back to Categories
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
              mr: 2,
            }}
          >
            {category === 'robots' ? (
              <SmartToy sx={{ fontSize: 32, color: 'white' }} />
            ) : (
              <Computer sx={{ fontSize: 32, color: 'white' }} />
            )}
          </Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
            {category === 'robots' ? 'Robot List' : 'Server List'}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          {loading ? 'Loading...' : (category === 'servers'
            ? `${serverIpAddresses.length} ${serverIpAddresses.length === 1 ? 'server' : 'servers'} found`
            : `${category === 'robots' ? groupRobotsByIpAddress(projects).length : projects.length} ${category === 'robots' ? 'robot IP addresses' : 'projects'} found`
          )}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#8b5cf6' }} />
          <Typography sx={{ ml: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
            Loading {category === 'robots' ? 'robots' : 'servers'}...
          </Typography>
        </Box>
      )}

      {!loading && category === 'servers' ? (
        // Render server IP addresses
        serverIpAddresses.length === 0 ? (
          <Card sx={{
            p: 4,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }}>
            <Typography variant="h6" sx={{ color: '#334155' }}>
              No server IP addresses found
            </Typography>
          </Card>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {serverIpAddresses.map((server, index) => (
              <Box key={server._id}>
                <Fade in timeout={500 + index * 100}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      position: 'relative',
                    }}
                    onClick={() => {
                      // Create a mock project object for IP addresses
                      const mockProject: Project = {
                        _id: server._id,
                        projectName: server.ipAddress,
                        isRobot: false,
                        ipAddress: server.ipAddress,
                        customName: server.customName,
                        latestCommit: {
                          commitHash: '',
                          commitMessage: `Server with ${server.projectCount} projects`,
                          authorName: 'System',
                          commitTimestamp: server.lastActivity,
                          branchName: 'main'
                        },
                        totalCommits: server.projectCount,
                        lastActivity: server.lastActivity
                      };
                      onProjectSelect(mockProject);
                    }}
                  >
                    {/* Three-dot menu button */}
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                      onClick={(e) => handleMenuOpen(e, server.ipAddress, 'server')}
                      size="small"
                    >
                      <MoreVert sx={{ color: '#475569', fontSize: 20 }} />
                    </IconButton>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {/* Header Section */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                            }}
                          >
                            <Storage sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="h5" 
                              fontWeight="bold" 
                              sx={{ 
                                color: '#1a202c',
                                mb: 0.5,
                                fontSize: '1.25rem'
                              }}
                            >
                              {server.customName || 'Server'}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#64748b', 
                                fontSize: '0.875rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              {server.ipAddress}
                            </Typography>
                        </Box>
                      </Box>

                        <Chip
                          label="Server"
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            height: '24px',
                            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
                          }}
                        />
                      </Box>

                      <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

                      {/* Server Info Section */}
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#6b7280', 
                            mb: 1.5, 
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Server Details
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule sx={{ fontSize: 16, color: '#8b5cf6' }} />
                            <Typography variant="body2" sx={{ color: '#1a202c', fontWeight: 500 }}>
                              {server.projectCount} {server.projectCount === 1 ? 'project' : 'projects'} running
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule sx={{ fontSize: 16, color: '#8b5cf6' }} />
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Last activity: {formatDate(server.lastActivity)}
                          </Typography>
                        </Box>
                      </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Box>
            ))}
          </Box>
        )
      ) : null}

      {!loading && category === 'robots' && (
        // Render robot projects grouped by IP address
        (() => {
          const groupedRobots = groupRobotsByIpAddress(projects);
          
          return groupedRobots.length === 0 ? (
            <Card sx={{
              p: 4,
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            }}>
              <Typography variant="h6" sx={{ color: '#334155' }}>
                No robots found
              </Typography>
            </Card>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 3,
              }}
            >
              {groupedRobots.map((group, index) => (
                <Box key={group.ipAddress}>
                  <Fade in timeout={500 + index * 100}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                        position: 'relative',
                      }}
                      onClick={() => {
                        // Create a mock project object for the IP address that contains all robots
                        const mockProject: Project = {
                          _id: group.ipAddress,
                          projectName: group.ipAddress,
                          isRobot: true,
                          ipAddress: group.ipAddress,
                          robotNames: group.robots.map(robot => 
                            robot.robotNames && robot.robotNames.length > 0 ? robot.robotNames[0] : robot.projectName
                          ),
                          latestCommit: group.latestCommit,
                          totalCommits: group.totalCommits,
                          lastActivity: group.lastActivity,
                          customName: group.customName
                        };
                        onProjectSelect(mockProject);
                      }}
                    >
                      {/* Three-dot menu button */}
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 10,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        onClick={(e) => handleMenuOpen(e, group.ipAddress, 'robot')}
                        size="small"
                      >
                        <MoreVert sx={{ color: '#475569', fontSize: 20 }} />
                      </IconButton>

                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        {/* Header Section */}
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2,
                                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                              }}
                            >
                              <Code sx={{ fontSize: 24, color: 'white' }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="h5" 
                                fontWeight="bold" 
                                sx={{ 
                                  color: '#1a202c',
                                  mb: 0.5,
                                  fontSize: '1.25rem'
                                }}
                              >
                                {group.customName || 'Robot Group'}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#64748b', 
                                  fontSize: '0.875rem',
                                  fontFamily: 'monospace'
                                }}
                              >
                                {group.ipAddress}
                              </Typography>
                          </Box>
                        </Box>

                          <Chip
                            label="Robot"
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              height: '24px',
                              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                            }}
                          />
                        </Box>

                        <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

                        {/* Robot Names Section */}
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6b7280', 
                              mb: 1.5, 
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            Robot Names
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {(() => {
                              // Get unique robot names
                              const uniqueRobotNames = Array.from(new Set(
                                group.robots.map(robot => 
                                  robot.robotNames && robot.robotNames.length > 0 
                                    ? robot.robotNames[0] 
                                    : robot.projectName
                                )
                              ));
                              
                              return uniqueRobotNames.map((robotName, index) => (
                                <Chip
                                  key={index}
                                  label={robotName}
                                  size="small"
                                  sx={{
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem',
                                    height: '28px',
                                    boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                />
                              ));
                            })()}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                </Box>
              ))}
            </Box>
          );
        })()
      )}

      {/* Three-dot Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
              fontSize: '0.95rem',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
              },
            },
          },
        }}
      >
        <MenuItem 
          onClick={handleAddServerName}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
          }}
        >
          <Label sx={{ mr: 1.5, fontSize: 20, color: '#8b5cf6' }} />
          <Typography 
            sx={{ 
              fontWeight: 'bold',
              color: '#1a202c',
              fontSize: '0.95rem'
            }}
          >
            {selectedItem?.type === 'robot' ? 'Update Robot Name' : 'Update Server Name'}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Add Server Name Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={(event, reason) => {
          // Only close on backdrop click or escape key, not on button clicks
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            setDialogOpen(false);
            setCustomName('');
            setSelectedItem(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={saving}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {selectedItem?.type === 'robot' ? 'Update Robot Name' : 'Update Server Name'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label={selectedItem?.type === 'robot' ? 'Robot Name' : 'Server Name'}
            fullWidth
            variant="outlined"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={`Enter a custom name for this ${selectedItem?.type === 'robot' ? 'robot' : 'server'}`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#8b5cf6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8b5cf6',
                },
              },
            }}
          />
          {selectedItem && (
            <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
              IP Address: {selectedItem.ipAddress}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setDialogOpen(false);
              setCustomName('');
              setSelectedItem(null);
            }}
            variant="outlined"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCustomName}
            variant="contained"
            disabled={saving}
            sx={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              },
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ProjectList;